import { useEffect, useRef } from 'react';

import { BrowserAuthError } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';

import { hasRequiredAccess } from 'auth/access';

/**
 * Handles the Entra ID redirect response. MSAL processes the response hash, then we
 * enforce app-role/group based access before landing the user in the portal.
 */
export function LoginCallback() {
  /**
   * Custom hooks
   */
  const { instance } = useMsal();
  const navigate = useNavigate();
  const hasHandled = useRef(false);

  /**
   * Effects
   */
  useEffect(() => {
    if (hasHandled.current) return;
    hasHandled.current = true;

    const handleRedirect = async () => {
      try {
        const response = await instance.handleRedirectPromise();
        const account =
          response?.account ?? instance.getAllAccounts()[0] ?? null;

        if (!account) {
          navigate('/login', { replace: true });
          return;
        }
        if (hasRequiredAccess(account)) {
          navigate('/', { replace: true });
        } else {
          // Authenticated but not authorized for the portal.
          instance.logoutRedirect({
            postLogoutRedirectUri: window.location.origin
          });
        }
      } catch (err) {
        if (
          err instanceof BrowserAuthError &&
          err.errorCode === 'interaction_in_progress'
        ) {
          sessionStorage.clear();
          window.location.href = '/login';
          return;
        }
        console.error('Login callback failed:', err);
        navigate('/login', { replace: true });
      }
    };

    handleRedirect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Render
   */
  return (
    <div
      className="bg-primary-1-100 w-full h-screen flex justify-center items-center text-white"
      data-testid="login_callback"
    >
      <p>Signing you in…</p>
    </div>
  );
}

export default LoginCallback;
